import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, User, Mail, Calendar, Clock, AlertCircle, DollarSign, CreditCard } from "lucide-react";
import { bookSoundTherapySession, verifySoundTherapyPayment } from "./soundTherapyApi";
import BookingConfirmationModal from "../../BookingConfirmationModal";
import toast from "react-hot-toast";

// Declare Razorpay for TypeScript
declare global {
    interface Window {
        Razorpay: any;
    }
}

interface UserDetails {
    name: string;
    email: string;
    age: string;
    phone: string;
}

interface SoundTherapyBookingProps {
    sessionData: any;
    onClose: () => void;
    onBookingSuccess: (bookingId: string) => void;
    sessionId: string;
    userDetails: UserDetails | null;
}

interface BookingFormData {
    name: string;
    email: string;
    age: string;
    gender: string;
    preferredTimeSlot: string;
    pricingOption: string;
    phone?: string;
}

interface PricingOption {
    type: 'session';
    label: string;
    price: number;
    description: string;
}

interface bookingRes {
    data: {
        bookingId: string;
        razorpayOrderId?: string;
        amount?: number;
        currency?: string;
        pendingBookingData?: any; // Add this for paid bookings
    };
    message: string;
}

interface RazorpayResponse {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
}

interface VerificationResponse {
    success: boolean;
    message: string;
    bookingId?: string; // Add this to get the actual booking ID after creation
}

export default function SoundTherapyBooking({ sessionData, onClose, onBookingSuccess, sessionId, userDetails }: SoundTherapyBookingProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<BookingFormData>({
        name: '',
        email: '',
        age: '',
        gender: '',
        preferredTimeSlot: '',
        pricingOption: 'session',
        phone: '',
    });

    useEffect(() => {
        if (userDetails) {
            setFormData(prev => ({
                ...prev,
                name: userDetails.name || '',
                email: userDetails.email || '',
                age: userDetails.age || '',
                phone: userDetails.phone || '',
            }));
        }
    }, [userDetails]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Partial<BookingFormData>>({});
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

    // Confirmation modal state
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingBookingData, setPendingBookingData] = useState<any>(null);

    // Load Razorpay script
    useEffect(() => {
        const loadRazorpay = () => {
            return new Promise((resolve) => {
                if (window.Razorpay) {
                    setIsRazorpayLoaded(true);
                    resolve(true);
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.onload = () => {
                    setIsRazorpayLoaded(true);
                    resolve(true);
                };
                script.onerror = () => {
                    resolve(false);
                };
                document.body.appendChild(script);
            });
        };

        loadRazorpay();
    }, []);

    const pricingOptions: PricingOption[] = [
        {
            type: 'session',
            label: 'Per Session',
            price: sessionData?.price || 500,
            description: 'Pay for a single sound therapy session.'
        }
    ];

    const selectedPricing = pricingOptions.find(option => option.type === formData.pricingOption);

    // Get available time slots from sessionData
    const getAvailableTimeSlots = () => {
        if (sessionData?.availableSlots && Array.isArray(sessionData.availableSlots)) {
            return sessionData.availableSlots.map((slot: any, index: number) => ({
                value: slot,
                label: `Slot ${index + 1}: ${slot}`
            }));
        }
        return [];
    };

    const timeSlots = getAvailableTimeSlots();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name as keyof BookingFormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<BookingFormData> = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Age validation
        if (!formData.age.trim()) {
            newErrors.age = 'Age is required';
        } else {
            const ageNum = parseInt(formData.age);
            if (isNaN(ageNum) || ageNum < 5 || ageNum > 100) {
                newErrors.age = 'Please enter a valid age between 5 and 100';
            }
        }

        // Gender validation
        if (!formData.gender) {
            newErrors.gender = 'Please select a gender';
        }

        // Preferred time slot validation
        if (!formData.preferredTimeSlot) {
            newErrors.preferredTimeSlot = 'Please select a preferred time slot';
        }

        // Pricing option validation
        if (!formData.pricingOption) {
            newErrors.pricingOption = 'Please select a pricing option';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare booking details for confirmation modal
        const bookingDetails = {
            serviceName: sessionData.title,
            serviceType: 'sound-therapy' as const,
            timeSlot: formData.preferredTimeSlot,
            amount: selectedPricing?.price || 0,
            pricingType: selectedPricing?.label,
            userDetails: {
                name: formData.name,
                email: formData.email,
                phone: formData.phone || '',
                age: formData.age,
                gender: formData.gender,
            },
        };

        const queryParams = new URLSearchParams(window.location.search);
        const urlVendorId = queryParams.get("vendorId") || queryParams.get("mentorId");
        if (urlVendorId) {
            sessionStorage.setItem("referralVendorId", urlVendorId);
        }
        const vendorId = urlVendorId || sessionStorage.getItem("referralVendorId") || undefined;

        // Prepare booking data for API call
        const bookingData = {
            sessionId: sessionId,
            sessionName: sessionData.title,
            vendorId: vendorId,
            ...formData,
            selectedPricing: selectedPricing,
            totalAmount: selectedPricing?.price || 0,
            bookingDate: new Date().toISOString(),
            status: selectedPricing?.price === 0 ? 'confirmed' : 'pending_payment',
        };

        setPendingBookingData({ bookingData, bookingDetails });
        setShowConfirmationModal(true);
    };

    const handleConfirmBooking = async () => {
        if (!pendingBookingData) return;

        setShowConfirmationModal(false);
        setIsSubmitting(true);
        setSubmitStatus('idle');

        // Add a timeout for the booking process
        const bookingTimeout = setTimeout(() => {
            setIsSubmitting(false);
            setSubmitStatus('error');
            toast.error("Booking is taking too long. Please try again.");
        }, 30000); // 30 seconds timeout

        try {
            const { bookingData } = pendingBookingData;

            // Call the API to create booking
            const response = await bookSoundTherapySession(bookingData) as bookingRes;
            clearTimeout(bookingTimeout); // Clear the timeout if the API responds in time

            if (response && response.data) {
                // For free bookings, booking is complete
                if (bookingData.totalAmount === 0) {
                    if (response.data.bookingId) {
                        toast.success('Free booking confirmed!');
                        setSubmitStatus('success');
                        setIsSubmitting(false);
                        onBookingSuccess(response.data.bookingId);
                        navigate('/profile');
                        onClose();
                    }
                } else {
                    // For paid bookings, initiate Razorpay payment
                    if (!isRazorpayLoaded || !response.data.razorpayOrderId) {
                        toast.error('Payment system not ready. Please try again.');
                        setIsSubmitting(false);
                        return;
                    }

                    if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
                        toast.error('Razorpay is not configured.');
                        setIsSubmitting(false);
                        return;
                    }

                    const options = {
                        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                        amount: response.data.amount,
                        currency: response.data.currency,
                        name: 'Metafit Wellness',
                        description: `Booking for ${sessionData.title}`,
                        image: '/metalogo.webp', // Optional: Add your logo
                        order_id: response.data.razorpayOrderId,
                        handler: async (razorpayResponse: RazorpayResponse) => {
                            // IMPORTANT: Now include pendingBookingData in verification
                            const paymentData = {
                                ...razorpayResponse,
                                bookingId: response.data.bookingId,
                                pendingBookingData: response.data.pendingBookingData // This contains all booking details
                            };

                            try {
                                const verificationRes = await verifySoundTherapyPayment(paymentData) as VerificationResponse;

                                if (verificationRes.success) {
                                    toast.success(verificationRes.message || 'Payment successful! Your booking is confirmed.');
                                    setSubmitStatus('success');
                                    setIsSubmitting(false);
                                    onBookingSuccess(verificationRes.bookingId || response.data.bookingId);
                                    navigate('/profile');
                                    onClose();
                                } else {
                                    toast.error(verificationRes.message || 'Payment verification failed. Please contact support.');
                                    setSubmitStatus('error');
                                    setIsSubmitting(false);
                                }
                            } catch (error: any) {
                                const errorMessage = error.response?.data?.message || 'An error occurred during payment verification.';
                                toast.error(errorMessage);
                                setSubmitStatus('error');
                                setIsSubmitting(false);
                            }
                        },
                        prefill: {
                            name: formData.name,
                            email: formData.email,
                            contact: formData.phone,
                        },
                        notes: {
                            bookingId: response.data.bookingId,
                            sessionId: sessionId,
                        },
                        theme: {
                            color: '#0d9488', // Teal color from your design
                        },
                        modal: {
                            ondismiss: async () => {
                                toast.error('Payment was cancelled.');
                                setIsSubmitting(false);
                                // No need to call cancelBookingAPI since no booking was created yet
                                // The booking will only be created after successful payment
                                console.log('Payment modal dismissed - no booking was created');
                            }
                        }
                    };

                    const rzp = new window.Razorpay(options);
                    rzp.open();

                    rzp.on('payment.failed', function (response: any) {
                        toast.error(`Payment failed: ${response.error.description}`);
                        setIsSubmitting(false);
                        // No booking was created, so no need to cancel anything
                    });
                }
            } else if (response.message) {
                toast.error(response.message);
                setSubmitStatus('error');
                setIsSubmitting(false);
            }
        } catch (error: any) {
            clearTimeout(bookingTimeout); // Clear the timeout on error as well
            console.error('Booking error:', error);
            const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
            setSubmitStatus('error');
            setIsSubmitting(false);
        }
    };
    return (
        <React.Fragment>
            <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Book Your Session</h2>
                    <p className="text-gray-600 text-sm mt-1">
                        {sessionData?.title || 'Sound Therapy Session'}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <X className="h-5 w-5 text-gray-500" />
                </button>
            </div>

            {/* Session Info Summary */}
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between text-sm">
                    <div>
                        <div className="font-medium text-gray-900">
                            {sessionData?.category || 'Sound Therapy'}
                        </div>
                        <div className="text-gray-600">
                            Duration: {sessionData?.duration || 'N/A'}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-bold text-lg text-teal-600">
                            {selectedPricing ?
                                (selectedPricing.price === 0 ? 'Free' : `₹${selectedPricing.price}`)
                                : 'Select pricing'
                            }
                        </div>
                        <div className="text-xs text-gray-500">
                            {timeSlots.length} time slots available
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="h-4 w-4 inline mr-1" />
                        Full Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Enter your full name"
                    />
                    {errors.name && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Email Field */}
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="h-4 w-4 inline mr-1" />
                        Email Address *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Enter your email address"
                    />
                    {errors.email && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.email}
                        </p>
                    )}
                </div>

                {/* Age and Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            Age *
                        </label>
                        <input
                            type="number"
                            id="age"
                            name="age"
                            value={formData.age}
                            onChange={handleInputChange}
                            min="5"
                            max="100"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.age ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            placeholder="Age"
                        />
                        {errors.age && (
                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {errors.age}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                            Gender *
                        </label>
                        <select
                            id="gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.gender ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                        {errors.gender && (
                            <p className="text-red-500 text-xs mt-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {errors.gender}
                            </p>
                        )}
                    </div>
                </div>
                
                {/* Phone Field */}
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone (Optional)
                    </label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        placeholder="Phone number"
                    />
                </div>

                {/* Pricing Options */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        <DollarSign className="h-4 w-4 inline mr-1" />
                        Select Pricing Option *
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                        {pricingOptions.map((option) => (
                            <div
                                key={option.type}
                                className={`relative border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${formData.pricingOption === option.type ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-gray-300 hover:border-gray-400'}`}
                                onClick={() => handleInputChange({ target: { name: 'pricingOption', value: option.type } } as any)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            name="pricingOption"
                                            value={option.type}
                                            checked={formData.pricingOption === option.type}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-teal-600 focus:ring-teal-500"
                                        />
                                        <div className="ml-3">
                                            <div className="font-medium text-gray-900">
                                                {option.label}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {option.description}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`font-bold text-lg ${option.price === 0 ? 'text-green-600' : 'text-teal-600'}`}>
                                            {option.price === 0 ? 'Free' : `₹${option.price}`}
                                        </div>
                                    </div>
                                </div>
                                {option.price > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="flex items-center text-xs text-gray-500">
                                            <CreditCard className="h-3 w-3 mr-1" />
                                            Secure payment via Razorpay
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {errors.pricingOption && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.pricingOption}
                        </p>
                    )}
                </div>

                {/* Preferred Time Slot */}
                <div>
                    <label htmlFor="preferredTimeSlot" className="block text-sm font-medium text-gray-700 mb-1">
                        <Clock className="h-4 w-4 inline mr-1" />
                        Preferred Time Slot *
                    </label>
                    <select
                        id="preferredTimeSlot"
                        name="preferredTimeSlot"
                        value={formData.preferredTimeSlot}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${errors.preferredTimeSlot ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                        <option value="">Select your preferred time slot</option>
                        {timeSlots.map((slot: any, index: any) => (
                            <option key={index} value={slot.value}>
                                {slot.label}
                            </option>
                        ))}
                        {timeSlots.length === 0 && (
                            <option value="flexible">Flexible with timing</option>
                        )}
                    </select>
                    {errors.preferredTimeSlot && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {errors.preferredTimeSlot}
                        </p>
                    )}
                </div>

                {/* Error Message */}
                {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center text-red-800">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            <span className="text-sm">
                                Something went wrong. Please try again or contact support.
                            </span>
                        </div>
                    </div>
                )}

                {/* Payment Security Info */}
                {selectedPricing && selectedPricing.price > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center text-blue-800 text-sm">
                            <CreditCard className="h-4 w-4 mr-2" />
                            <span>
                                Secure payment processing by Razorpay. Your payment information is encrypted and safe.
                            </span>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex space-x-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={isSubmitting}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting || (selectedPricing && selectedPricing.price > 0 && !isRazorpayLoaded)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-lg hover:from-teal-600 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105">
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {selectedPricing?.price === 0 ? 'Booking...' : 'Processing Payment...'}
                            </span>
                        ) : (
                            <>
                                {selectedPricing?.price === 0 ? (
                                    `Book Now (Free)`
                                ) : selectedPricing ? (
                                    <span className="flex items-center justify-center">
                                        <CreditCard className="h-4 w-4 mr-1" />
                                        Pay ₹{selectedPricing.price}
                                    </span>
                                ) : (
                                    'Select pricing to continue'
                                )}
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Terms */}
            <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 text-center">
                    By booking this class, you agree to our {' '}
                    <a href="#" className="text-teal-600 hover:underline">Terms of Service</a> and {' '}
                    <a href="#" className="text-teal-600 hover:underline">Privacy Policy</a>.
                    You will receive a confirmation email with class details.
                </p>
            </div>
        </div>

        {/* Booking Confirmation Modal */}
        {showConfirmationModal && pendingBookingData && (
            <BookingConfirmationModal
                isOpen={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirmBooking}
                bookingDetails={pendingBookingData.bookingDetails}
                isProcessing={isSubmitting}
            />
        )}
        </React.Fragment>
    );
}
